import { useEffect, useState } from 'react';
import { Upload, Loader2, CheckCircle, Clock, XCircle, FileText } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card } from '../../new-components/ui/card';
import { Badge } from '../../new-components/ui/badge';
import { Button } from '../../new-components/ui/button';
import api from '../../../services/api';
import { unwrap } from './shared';
import { SETTINGS_PANEL, SectionHeader, PRIMARY_BTN } from '../ui';

/**
 * Accreditation Verification — self-contained Settings section. Owns its own status/docs
 * fetch and the upload flow. Extracted verbatim from AccountSettings (no behavior change); the
 * fetch runs on mount here since the component only mounts when the accreditation tab is active.
 */
export function AccreditationSection() {
  const [accreditationStatus, setAccreditationStatus] = useState<any>(null);
  const [accreditationDocs, setAccreditationDocs] = useState<any[]>([]);
  const [accredLoading, setAccredLoading] = useState(false);
  const [accredUploading, setAccredUploading] = useState(false);
  const [accredFile, setAccredFile] = useState<File | null>(null);

  useEffect(() => {
    let ignore = false;
    const fetchAccreditation = async () => {
      setAccredLoading(true);
      try {
        const [statusRes, docsRes] = await Promise.all([
          api.get('/accreditation/status'),
          api.get('/accreditation/my'),
        ]);
        if (ignore) return;
        const s = unwrap(statusRes);
        setAccreditationStatus(s?.data || s);
        const d = unwrap(docsRes);
        setAccreditationDocs(d?.data?.documents || d?.documents || []);
      } catch (err) {
        if (!ignore) console.error('Failed to load accreditation data:', err);
      } finally {
        if (!ignore) setAccredLoading(false);
      }
    };
    fetchAccreditation();
    return () => { ignore = true; };
  }, []);

  const handleAccreditationUpload = async () => {
    if (!accredFile) return;
    setAccredUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', accredFile);
      await api.post('/accreditation/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Accreditation document uploaded successfully');
      setAccredFile(null);
      // Refresh docs
      const docsRes = await api.get('/accreditation/my');
      const d = unwrap(docsRes);
      setAccreditationDocs(d?.data?.documents || d?.documents || []);
      const statusRes = await api.get('/accreditation/status');
      const s = unwrap(statusRes);
      setAccreditationStatus(s?.data || s);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Upload failed');
    } finally {
      setAccredUploading(false);
    }
  };

  return (
    <Card className="p-6 border-border">
      <p className="text-sm text-muted-foreground">
        Upload documentation to verify your accredited investor status. Accepted documents include CPA letters, tax returns, or financial statements.
      </p>

      {accredLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          {/* Status — inline, only when meaningful */}
          {accreditationStatus?.status === 'verified' && (
            <p className="flex items-center gap-2 text-sm font-medium text-[hsl(var(--success))]">
              <CheckCircle className="h-4 w-4" aria-hidden="true" />
              Verified{accreditationStatus?.expires_at ? ` · Expires ${new Date(accreditationStatus.expires_at).toLocaleDateString()}` : ''}
            </p>
          )}
          {accreditationStatus?.status === 'pending_review' && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" aria-hidden="true" />
              Your documents are under review
            </p>
          )}
          {accreditationStatus?.status === 'rejected' && (
            <p className="flex items-center gap-2 text-sm text-destructive">
              <XCircle className="h-4 w-4" aria-hidden="true" />
              Your submission was not approved. Please upload updated documents.
            </p>
          )}

          {/* Upload Section */}
          {accreditationStatus?.status !== 'verified' && (
            <div className="border-2 border-dashed border-border rounded-lg p-6">
              <div className="text-center">
                <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm font-medium mb-1">Upload Accreditation Document</p>
                <p className="text-xs text-muted-foreground mb-4">
                  PDF, JPG, PNG, DOC, or DOCX • Max 20MB
                </p>
                <input
                  type="file"
                  id="accred-upload"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size > 20 * 1024 * 1024) {
                        toast.error('File size must be under 20MB');
                        return;
                      }
                      setAccredFile(file);
                    }
                  }}
                />
                {accredFile ? (
                  <div className="space-y-3">
                    <div className="flex min-w-0 items-center gap-2 text-sm">
                      <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                      <span className="min-w-0 truncate">{accredFile.name}</span>
                      <span className="flex-shrink-0 text-muted-foreground">({Math.round(accredFile.size / 1024)} KB)</span>
                      <button onClick={() => setAccredFile(null)} className="ml-1 flex-shrink-0 text-destructive hover:opacity-80">
                        <XCircle className="h-4 w-4" />
                      </button>
                    </div>
                    <Button onClick={handleAccreditationUpload} disabled={accredUploading} className={PRIMARY_BTN}>
                      {accredUploading ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                      ) : (
                        <><Upload className="h-4 w-4 mr-2" /> Upload Document</>
                      )}
                    </Button>
                  </div>
                ) : (
                  <Button variant="outline" onClick={() => document.getElementById('accred-upload')?.click()}>
                    Choose File
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Uploaded Documents */}
          {accreditationDocs.length > 0 && (
            <div>
              <SectionHeader title="Uploaded Documents" />
              <div className="space-y-2">
                {accreditationDocs.map((doc: any) => (
                  <div key={doc.id} className={`${SETTINGS_PANEL} flex items-center justify-between gap-3 p-3`}>
                    <div className="flex min-w-0 items-center gap-3">
                      <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-foreground">{doc.title || doc.original_filename || 'Document'}</p>
                        <p className="text-xs text-muted-foreground">
                          {doc.file_size_bytes ? `${Math.round(doc.file_size_bytes / 1024)} KB` : ''}
                          {doc.created_at ? ` · ${new Date(doc.created_at).toLocaleDateString()}` : ''}
                        </p>
                      </div>
                    </div>
                    <Badge variant="secondary" className={`flex-shrink-0 text-xs capitalize ${
                      doc.status === 'verified' ? 'bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]' :
                      doc.status === 'rejected' ? 'bg-destructive/10 text-destructive' :
                      'bg-secondary text-muted-foreground'
                    }`}>
                      {doc.status || 'uploaded'}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export default AccreditationSection;
